import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { AgGridReact } from "ag-grid-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CellClassParams,
  CellEditingStoppedEvent,
  CellEditRequestEvent,
  CellStyle,
  ColDef,
  EditableCallbackParams,
  GetRowIdParams,
  GridReadyEvent,
  IRowNode,
  SelectionChangedEvent,
} from "ag-grid-community";
import { Box } from "@chakra-ui/react";
import { times, uniq } from "lodash";
import { produce } from "immer";
import {
  CellEditingStartedEvent,
  CellKeyDownEvent,
  CellMouseOutEvent,
  CellMouseOverEvent,
} from "ag-grid-community/dist/lib/events";
import ProductImageCell, {
  PRODUCT_IMAGE_SIZE,
} from "@/features/order-sheet-sets/product-image-cell";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import {
  FlatProduct,
  FlatSheetKey,
  flatTabsFamily,
  inflatedTabsFamily,
  lineSheetSetFamily,
  orderSheetHasDiscountConditionFamily,
} from "@/features/line-sheet-sets/helpers/sheet-state";
import useInflatedTabs from "@/features/line-sheet-sets/helpers/use-inflated-tabs";
import ProductCheckboxCell from "@/features/order-sheet-sets/product-checkbox-cell";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import SheetHeader from "@/features/order-sheet-sets/sheet-header";
import SheetCheckBoxHeader from "@/features/order-sheet-sets/sheet-checkbox-header";
import ProductImageHeader from "@/features/order-sheet-sets/product-image-header";
import { MAX_PRODUCT_IMAGE_COUNT } from "@/features/order-sheet-sets/representative-product-image";
import OverlappedValueCell from "@/features/ui/overlapped-value-cell";
import OverlappedNumberEditor from "@/features/ui/overlapped-number-editor";
import OverlappedConditionTypeCell from "@/features/ui/overlapped-condition-type-cell";
import OrderSheetIvaCell from "@/features/order-sheet-sets/order-sheet-iva-cell";
import TakeAllProductsCell from "@/features/ui/take-all-products-cell";
import HighlightCellRenderer from "@/shared/components/ag-grid/highlight-cell-renderer";
import { DenseOrderSheetSet } from "@/features/order-sheets/order-sheet.type";
import {
  isOrderSheetPageCurrencyKrw,
  orderSheetPageSearchKeywordStore,
} from "@/features/order-sheet-sets/recoils/order-sheet-page-store";
import { exchangeCurrencyStore } from "@/shared/store/exchangeCurrencyStore";
import useOrderSheetSetPagePersistedQuery from "@/features/order-sheet-sets/hooks/use-order-sheet-set-page-persisted-query";
import NoResults from "@/shared/components/no-results";
import { EMPTY_SHEET_TAB_FILTER_QUERY } from "@/features/order-sheet-sets/sheet-tab-filter";
import HtmlCellRenderer from "@/shared/components/ag-grid/html-cell-renderer";
import { OrderSheetTabBottomBar } from "@/features/order-sheet-sets/order-sheet-tab-bottom-bar";

interface OrderSheetTabProps {
  sheetKey: FlatSheetKey;
  lineSheetSetId: number;
  tabIndex: number;
  isLoading: boolean;
  onEditing?: (isEditing: boolean) => void;
  onSelectedProductIdsChange: (ids: number[]) => void;
  onGridReady: (event: GridReadyEvent<FlatProduct>) => void;
  isOrderEditable?: boolean;
  isConfirmedEditable?: boolean;
  isConditionEditable?: boolean;
  onUpdatingInflatedProductComplete: (isCompleted: boolean) => void;
  orderSheetSet?: DenseOrderSheetSet;
  selectOrderSheetId: number;
  revisionNumber?: number;
}

function getRowId(params: GetRowIdParams<FlatProduct>) {
  return params.data.rowId;
}

function isEditableCell(isEditable: boolean, params: any) {
  if (isEditable) {
    const rowIndex = params.node.rowIndex;
    const i = parseInt(params.column.getId().split("_")[1]);
    if (rowIndex !== null && rowIndex % 3 === 2) {
      const stockRow = params.api.getDisplayedRowAtIndex(rowIndex - 1);
      if (stockRow && stockRow.data && stockRow.data.sizes) {
        const stockQuantity = stockRow.data.sizes[i] as number;
        return stockQuantity === -1 || stockQuantity > 0;
      }
    }
  }
  return false;
}

function OrderSheetTab({
  sheetKey,
  lineSheetSetId,
  tabIndex,
  isLoading,
  isOrderEditable = false,
  onEditing,
  onSelectedProductIdsChange,
  onGridReady,
  isConfirmedEditable = false,
  isConditionEditable = false,
  onUpdatingInflatedProductComplete,
  orderSheetSet,
  selectOrderSheetId,
  revisionNumber,
}: OrderSheetTabProps) {
  const { warning: showWarning } = useAppToasts();

  const flatSheetState = useRecoilValue(flatTabsFamily(sheetKey));

  const lineSheetSetState = useRecoilValue(
    lineSheetSetFamily({ lineSheetSetId: lineSheetSetId })
  );

  const rowData = useMemo(() => {
    if (flatSheetState && tabIndex > -1) {
      return flatSheetState.tabs
        .find((tab) => tab.index === tabIndex)
        ?.products.filter((product) => product.isFiltered);
    }
    return undefined;
  }, [flatSheetState, tabIndex]);

  const { value: query, set: setQuery } = useOrderSheetSetPagePersistedQuery();

  const { updateInflatedProduct, getInflatedProductById } = useInflatedTabs();
  const gridRef = useRef<AgGridReact<FlatProduct> | null>(null);
  const [isGridRefSet, setIsGridRefSet] = useState(false);

  const { t, tCurrencyString } = useI18nHelper();

  const setOrderSheetSearchKeyword = useSetRecoilState(
    orderSheetPageSearchKeywordStore
  );

  const [isImagesCollapsed, setIsImagesCollapsed] = useState<boolean>(true);

  const components = useMemo<{ [p: string]: any }>(() => {
    return {
      agColumnHeader: SheetHeader,
    };
  }, []);

  const isKrw = useRecoilValue(isOrderSheetPageCurrencyKrw);
  // DISCOUNT 컨디션 상품이 하나도 없으면 IVA 컬럼은 빈 칸만 남아서 통째로 감춘다.
  const hasDiscountCondition = useRecoilValue(
    orderSheetHasDiscountConditionFamily({ key: sheetKey, tabIndex })
  );
  const defaultCurrency = lineSheetSetState?.lineSheetSet.currency || "EUR";
  const selectedCurrency = useMemo(() => {
    return isKrw ? "KRW" : lineSheetSetState?.lineSheetSet.currency || "EUR";
  }, [isKrw, lineSheetSetState]);
  const exchangeCurrencyInfo = useRecoilValue(exchangeCurrencyStore);
  const exchangeCurrencyValue = useMemo(() => {
    return defaultCurrency !== selectedCurrency ? exchangeCurrencyInfo[defaultCurrency]?.pathrade_send_rate : 1;
  }, [defaultCurrency, exchangeCurrencyInfo, selectedCurrency]);

  const [colDefs, setColDefs] = useState<ColDef<FlatProduct>[]>([
    {
      cellRenderer: ProductCheckboxCell,
      rowSpan: (params) => {
        if (params.node && params.node.rowIndex !== null) {
          return params.node.rowIndex % 3 === 0 ? 3 : 1;
        }
        return 1;
      },
      width: 40,
      pinned: "left",
      field: "isChecked",
      headerName: "",
      resizable: false,
      suppressMovable: true,
      headerCheckboxSelection: false,
      showDisabledCheckboxes: false,
      headerComponent: SheetCheckBoxHeader,
      cellClassRules: {
        "product-odd-row": (params) => params.rowIndex % 6 === 0,
        "product-even-row": (params) => params.rowIndex % 6 === 3,
        "product-checkbox-cell": (params) => params.rowIndex % 3 === 0,
      },
      headerClass: "product-checkbox-header",
    },
    {
      valueGetter: (params) => {
        if (params.data) {
          return {
            imageUrls: params.data.imageUrls?.map((imageUrl) => {
              return {
                url: imageUrl.url,
                type: imageUrl.type,
              };
            }),
            id: params.data?.id,
            searchWord: params.data?.searchWord,
          };
        }
        return null;
      },
      cellStyle: { fontSize: "12px" },
      cellRenderer: ProductImageCell,
      width: PRODUCT_IMAGE_SIZE,
      autoHeight: false,
      rowSpan: (params) => {
        if (params.node && params.node.rowIndex !== null) {
          return params.node.rowIndex % 3 === 0 ? 3 : 1;
        }
        return 1;
      },
      resizable: false,
      pinned: "left",
      field: "i",
      headerName: "",
      headerClass: "product-image-header",
      headerComponent: ProductImageHeader,
      headerComponentParams: {
        isCollapsed: isImagesCollapsed,
        onClick: (isCollapse: boolean) => {
          setIsImagesCollapsed(isCollapse);
        },
      },
      cellRendererParams: {
        isCollapsed: isImagesCollapsed,
        isLineSheet: false,
      },
      cellClassRules: {
        "product-odd-row": (params) => params.rowIndex % 6 === 0,
        "product-even-row": (params) => params.rowIndex % 6 === 3,
      },
    },
    {
      field: "brandSKUEtc",
      autoHeaderHeight: true,
      width: 160,
      resizable: true,
      pinned: "left",
      cellStyle: { fontSize: "12px" },
      headerComponentParams: {
        items: ["brand", "sku", "etc_info"],
      },
      cellRenderer: HighlightCellRenderer,
      cellClassRules: {
        "product-odd-row": (params) => params.rowIndex % 6 < 3,
        "product-even-row": (params) => params.rowIndex % 6 >= 3,
      },
    },
    {
      field: "genderCategorySubcategory",
      width: 120,
      resizable: true,
      pinned: "left",
      cellStyle: { fontSize: "12px" },
      headerComponentParams: {
        items: ["gender", "category", "subcategory"],
      },
      cellRenderer: HighlightCellRenderer,
      cellClassRules: {
        "product-odd-row": (params) => params.rowIndex % 6 < 3,
        "product-even-row": (params) => params.rowIndex % 6 >= 3,
      },
    },
    {
      field: "colorCodeColorNameMaterial",
      width: 120,
      resizable: true,
      pinned: "left",
      cellStyle: { fontSize: "12px" },
      headerComponentParams: {
        items: ["season", "color", "material"],
      },
      cellRenderer: HighlightCellRenderer,
      cellClassRules: {
        "product-odd-row": (params) => params.rowIndex % 6 < 3,
        "product-even-row": (params) => params.rowIndex % 6 >= 3,
      },
    },
    {
      field: "searchLinkIcons",
      width: 120,
      resizable: true,
      pinned: "left",
      cellStyle: { fontSize: "12px" },
      headerComponentParams: {
        items: ["", "search_link_icons", ""],
      },
      hide: lineSheetSetState?.lineSheetSet.type === "SEASON",
      cellClassRules: {
        "product-odd-row": (params) => params.rowIndex % 6 < 3,
        "product-even-row": (params) => params.rowIndex % 6 >= 3,
      },
      cellRenderer: HtmlCellRenderer,
      headerClass: "header-center", //우측 정렬
    },
    {
      field: "priceAmount",
      cellStyle: (params): CellStyle => {
        const cellStyle: CellStyle = {
          textAlign: "end",
          fontSize: "11px",
          fontWeight: "400",
          color: "#54565F",
          padding: "0",
        };
        const rowIndex = params.node?.rowIndex;
        if (typeof rowIndex === "number" && rowIndex % 3 === 0) {
          cellStyle.color = "#303136";
          cellStyle.fontSize = "12px";
          cellStyle.fontWeight = "700";
        }
        return cellStyle;
      },
      width: 120,
      resizable: false,
      valueGetter: (params) => {
        const value = params.data?.priceAmount;
        const currency =
          params.data?.priceCurrency ||
          lineSheetSetState?.lineSheetSet?.currency;
        const exchangeCurrency = currency ? exchangeCurrencyInfo[currency]?.pathrade_send_rate : 0;
        if (value !== undefined) {
          // 가격이 없는 행(예: 도매가 없음)은 0 대신 "-"
          if (typeof value !== "object" && params.data?.isPriceMissing) {
            return "-";
          }
          if (typeof value !== "object") {
            if (currency) {
              return tCurrencyString(
                isKrw ? Math.round(value * exchangeCurrency) : value,
                {
                  type: "SYMBOL",
                  code: isKrw ? "KRW" : currency,
                }
              );
            } else {
              return tCurrencyString(
                isKrw ? Math.round(value * exchangeCurrency) : value
              );
            }
          } else {
            if (currency) {
              return {
                top: tCurrencyString(
                  isKrw ? Math.round(value.top * exchangeCurrency) : value.top,
                  {
                    type: "SYMBOL",
                    code: isKrw ? "KRW" : currency,
                  }
                ),
                under: tCurrencyString(
                  isKrw
                    ? Math.round(value.under * exchangeCurrency)
                    : value.under,
                  {
                    type: "SYMBOL",
                    code: isKrw ? "KRW" : currency,
                  }
                ),
              };
            } else {
              return {
                top: tCurrencyString(
                  isKrw ? Math.round(value.top * exchangeCurrency) : value.top
                ),
                under: tCurrencyString(
                  isKrw
                    ? Math.round(value.under * exchangeCurrency)
                    : value.under
                ),
              };
            }
          }
        }
      },
      cellRendererSelector: (params) => {
        const value = params.data?.priceAmount;
        if (value !== undefined) {
          if (typeof value == "object") {
            {
              return {
                component: OverlappedValueCell,
                params: {
                  topTextAlign: "end",
                  fontWeight: "700",
                },
              };
            }
          }
        }
        return undefined;
      },
      pinned: "left",
      headerComponentParams: {
        items: ["supply_price", "cost", "retail"],
      },
      headerClass: "header-right", //우측 정렬
      cellClassRules: {
        "product-odd-row": (params) => params.rowIndex % 6 < 3,
        "product-even-row": (params) => params.rowIndex % 6 >= 3,
        "product-price-cell": (params) => params.rowIndex % 3 === 0,
        "product-not-selectable-cell": (params) => {
          const rowIndex = params.node.rowIndex;
          if (
            params.rowIndex % 3 === 0 &&
            rowIndex !== null &&
            params.node.parent?.childrenAfterFilter?.[rowIndex]?.data
              ?.priceAmount && // supply price
            !params.node.parent?.childrenAfterFilter?.[rowIndex + 1]?.data
              ?.priceAmount && // cost
            !params.node.parent?.childrenAfterFilter?.[rowIndex + 2]?.data
              ?.priceAmount // retail
          ) {
            return true;
          }
          return false;
        },
      },
    },
    {
      // 상품 단위 IVA(적용 여부 + 계산식) — 컨디션 컬럼과 동일한 3행 몰드, DISCOUNT일 때만 노출.
      // 컨디션 컬럼 '앞'에 둬야 sq 컬럼 동적 삽입 시 함께 보존됨(slice(0, conditionIdx+1)).
      headerName: "IVA",
      colId: "ivaCondition",
      pinned: "left",
      width: 100,
      resizable: false,
      editable: false,
      cellStyle: {
        overflow: "visible",
        padding: "1px",
        paddingLeft: "4px",
      },
      cellRenderer: OrderSheetIvaCell,
      valueGetter: (params) => {
        // 표시/재렌더는 OrderSheetIvaCell이 Recoil(orderSheetIvaConditionFamily)을
        // 직접 구독해 처리하므로 여기서는 값만 노출(셀 렌더러는 이 값을 쓰지 않음).
        const moldIndex =
          params.node != null && params.node.rowIndex != null
            ? params.node.rowIndex % 3
            : -1;
        if (moldIndex === 0) return params.data?.applyIva;
        if (moldIndex === 1) return params.data?.ivaValue;
        return null;
      },
      cellClassRules: {
        "product-odd-row": (params) => params.rowIndex % 6 < 3,
        "product-even-row": (params) => params.rowIndex % 6 >= 3,
      },
    },
    {
      field: "priceAdjustedByPartial",
      headerName: "",
      pinned: "left",
      width: 104,
      resizable: false,
      colId: "priceAdjustedByPartial",
      // cellRenderer: PriceAdjustedByCell,
      cellStyle: {
        overflow: "visible",
        padding: "1px",
        paddingLeft: "8px",
      },
      valueGetter: (params) => {
        //cell re render가 되려면 이 값이 변경되어함,
        //custom cell 에서 value로 접근 가능
        const moldIndex =
          params.node != null && params.node.rowIndex != null
            ? params.node.rowIndex % 3
            : -1;
        if (moldIndex === 2) {
          return params.data?.sizes;
        }

        return params.data?.priceAdjustedByPartial;
      },
      cellRendererSelector: (params) => {
        const rowIndex = params.node.rowIndex;
        if (rowIndex != null) {
          const index = rowIndex % 3;
          if (index === 0) {
            return {
              component: OverlappedConditionTypeCell,
              params: {
                isConditionFixed: params.data?.isConditionFixed,
              }
            };
          } else if (index === 1) {
            return {
              component: OverlappedValueCell,
              params: {
                unit: "%",
                fontWeight: "500",
              },
            };
          } else if (index === 2) {
            return {
              component: TakeAllProductsCell,
            };
          }
        }

        return undefined;
      },
      cellEditor: OverlappedNumberEditor,
      cellEditorParams: {
        unit: "%",
      },
      editable: (params) => {
        return (!params?.data?.isConditionFixed && params?.node?.rowIndex && params?.node?.rowIndex % 3 === 1) || true;
      },
      cellClass: "product-price-adjusted-cell",
      cellClassRules: {
        "product-odd-row": (params) => params.rowIndex % 6 < 3,
        "product-even-row": (params) => params.rowIndex % 6 >= 3,
        "product-price-adjusted-selectbox-cell": (params) =>
          params.rowIndex % 3 === 0,
        "product-price-adjusted-input-cell": (params) =>
          params.rowIndex % 3 === 1,
        "product-editable-cell": (params) => {
          return params.rowIndex % 3 === 1 && !params?.data?.isConditionFixed;
        },
        "product-not-selectable-cell": (params) => {
          if (
            params.rowIndex % 3 === 1 &&
            params.node.parent?.childrenAfterFilter?.[params.rowIndex - 1]?.data
              ?.priceAmount && // supply price
            !params.node.parent?.childrenAfterFilter?.[params.rowIndex]?.data
              ?.priceAmount && // cost
            !params.node.parent?.childrenAfterFilter?.[params.rowIndex + 1]
              ?.data?.priceAmount // retail
          ) {
            return true;
          }
          return false;
        },
      },
    },
  ]);

  const maxProductImageCount = useMemo(() => {
    return (
      rowData
        ?.map((i) =>
          Math.min(MAX_PRODUCT_IMAGE_COUNT, (i.imageUrls || []).length)
        )
        .reduce((acc, cur) => Math.max(acc, cur), 0) || 0
    );
  }, [rowData]);

  useEffect(() => {
    setColDefs((prev) =>
      prev.map((def) =>
        def.field === "priceAmount"
          ? {
            ...def,
            valueGetter: (params) => {
              const value = params.data?.priceAmount;
              const currency =
                params.data?.priceCurrency ||
                lineSheetSetState?.lineSheetSet?.currency;
              if (!exchangeCurrencyInfo) {
                return
              }
              const exchangeCurrency = exchangeCurrencyInfo[currency || "EUR"]?.pathrade_send_rate;
              if (value !== undefined) {
                // 가격이 없는 행(예: 도매가 없음)은 0 대신 "-"
                if (typeof value !== "object" && params.data?.isPriceMissing) {
                  return "-";
                }
                if (typeof value !== "object") {
                  if (currency) {
                    return tCurrencyString(
                      isKrw ? Math.round(value * exchangeCurrency) : value,
                      {
                        type: "SYMBOL",
                        code: isKrw ? "KRW" : currency,
                      }
                    );
                  } else {
                    return tCurrencyString(
                      isKrw ? Math.round(value * exchangeCurrency) : value
                    );
                  }
                } else {
                  if (currency) {
                    return {
                      top: tCurrencyString(
                        isKrw
                          ? Math.round(value.top * exchangeCurrency)
                          : value.top,
                        {
                          type: "SYMBOL",
                          code: isKrw ? "KRW" : currency,
                        }
                      ),
                      under: tCurrencyString(
                        isKrw
                          ? Math.round(value.under * exchangeCurrency)
                          : value.under,
                        {
                          type: "SYMBOL",
                          code: isKrw ? "KRW" : currency,
                        }
                      ),
                    };
                  } else {
                    return {
                      top: tCurrencyString(
                        isKrw
                          ? Math.round(value.top * exchangeCurrency)
                          : value.top
                      ),
                      under: tCurrencyString(
                        isKrw
                          ? Math.round(value.under * exchangeCurrency)
                          : value.under
                      ),
                    };
                  }
                }
              }
            },
          }
          : def
      )
    );
  }, [isKrw, lineSheetSetState, tCurrencyString, exchangeCurrencyInfo]);

  useEffect(() => {
    setColDefs((prev) => {
      return produce(prev, (draft) => {
        const i = draft.findIndex((colDef) => colDef.field === "i");

        let width;
        if (isImagesCollapsed) {
          width = PRODUCT_IMAGE_SIZE;
        } else {
          width =
            PRODUCT_IMAGE_SIZE * maxProductImageCount +
            (maxProductImageCount - 1) * 4 +
            2;
        }
        if (i > -1) {
          draft[i] = {
            ...draft[i],
            width,
            headerComponentParams: {
              ...draft[i].headerComponentParams,
              isCollapsed: isImagesCollapsed,
              isDisabled:
                maxProductImageCount === 0 || maxProductImageCount === 1,
            },
            cellRendererParams: {
              ...draft[i].cellRendererParams,
              isCollapsed: isImagesCollapsed,
            },
          };
        }
      });
    });
  }, [isImagesCollapsed, maxProductImageCount]);

  useEffect(() => {
    //cell params변경 or colDef수정이 필요한경우,

    setColDefs((prev) => {
      return produce(prev, (draft) => {
        const i = draft.findIndex((c) => c.field === "priceAdjustedByPartial");
        if (i > -1) {
          draft[i].cellRendererParams = {
            tabIndex,
            sheetKey,
            isEditable: isConditionEditable,
            isOrderEditable,
            // 에이전시: isConditionFixed여도 컨디션 타입 편집 허용
            ignoreConditionFixed: true,
          };
          draft[i].editable = (params => {
            const moldIndex =
              params.node.rowIndex != null ? params.node.rowIndex % 3 : -1;
            if (moldIndex === 1) {
              return isConditionEditable;
            }
            return false;
          });
          draft[i].cellClassRules!["product-editable-cell"] = (params) => {
            const moldIndex =
              (params.node.rowIndex != null ? params.node.rowIndex : -1) % 3;
            if (moldIndex === 1) {
              return isConditionEditable;
            }
            return false;
          }
        }
        const ivaIdx = draft.findIndex((c) => c.colId === "ivaCondition");
        if (ivaIdx > -1) {
          draft[ivaIdx].hide = !hasDiscountCondition;
          draft[ivaIdx].cellRendererParams = {
            tabIndex,
            sheetKey,
            isEditable: isConditionEditable,
            onUpdatingInflatedProductComplete,
          };
        }
      });
    });
  }, [
    tabIndex,
    sheetKey,
    isOrderEditable,
    isConfirmedEditable,
    isConditionEditable,
    hasDiscountCondition,
    onUpdatingInflatedProductComplete,
  ]);

  const sizeLength = useMemo(() => {
    if (rowData) {
      return rowData
        .map((i) => {
          return i.sizes ? i.sizes.length : 0;
        })
        .reduce((acc, cur) => {
          return Math.max(acc, cur);
        }, 0);
    }
    return 0;
  }, [rowData]);

  const isSizeOrQuantityEditable = useCallback(
    (
      params: CellClassParams<FlatProduct> | EditableCallbackParams<FlatProduct>
    ) => {
      const colIndex = parseInt(params.column.getColId().split("_")[1]);
      const index =
        params.node.rowIndex != null ? params.node.rowIndex % 3 : -1;
      if (index === 1) {
        return (
          isConfirmedEditable && params.data?.sizes![colIndex] !== undefined
        );
      } else if (index === 2) {
        if (!isOrderEditable || params.data?.sizes![colIndex] === undefined) {
          return false;
        }
        // 품절(가용재고 0) 옵션은 주문수량 입력도 막는다 — 재고는 바로 윗행 sizes에 있다.
        // (재고 행을 못 찾는 예외 상황에서는 기존처럼 입력을 허용한다)
        const stockRow = params.api.getDisplayedRowAtIndex(
          params.node.rowIndex! - 1
        );
        const stockQuantity = stockRow?.data?.sizes?.[colIndex];
        return stockQuantity === -1 || Number(stockQuantity ?? 1) > 0;
      }
      return false;
    },
    [isConfirmedEditable, isOrderEditable]
  );

  useEffect(() => {
    if (sizeLength > 0) {
      setColDefs((prev) => {
        const i = prev.findLastIndex(
          (p) => p.field === "priceAdjustedByPartial"
        );

        if (i > -1) {
          return [
            ...prev.slice(0, i + 1),
            ...((): ColDef<FlatProduct>[] => {
              return times(sizeLength, (index): ColDef<FlatProduct> => {
                return {
                  headerName: `sq${index}`,
                  headerClass: "product-size-or-quantity-cell-header",
                  colId: `sq_${index}`,
                  width: 80,
                  cellStyle: (params) => {
                    return {
                      textAlign: "center",
                      fontSize: "12px",
                      padding: 0,
                    };
                  },
                  valueGetter: (params) => {
                    const v = params.data?.sizes?.[index];
                    if (v === 0) {
                      // 재고(가운데) 행의 0 = 가용재고 차감 결과 품절. 빈 칸이면 옵션이
                      // 없는 것과 구분이 안 돼 '품절'로 표시한다. 주문수량 행의 0은 기존대로 빈 칸.
                      const rowIndex = params.node?.rowIndex;
                      return rowIndex != null && rowIndex % 3 === 1
                        ? t("sold_out")
                        : "";
                    }
                    return v;
                  },
                  valueFormatter: (params) => {
                    if (params.value === -1) {
                      return "∞";
                    }
                    return params.value === 0 ? "" : params.value;
                  },
                  valueSetter: (params) => {
                    return true;
                  },
                  cellRenderer: OverlappedValueCell,
                  cellEditor: OverlappedNumberEditor,
                  cellClassRules: {
                    "product-odd-row": (params) => params.rowIndex % 6 < 3,
                    "product-even-row": (params) => params.rowIndex % 6 >= 3,
                    "product-each-last-row": (params) =>
                      params.rowIndex % 3 === 2,
                    "product-editable-cell": (params) => {
                      return isSizeOrQuantityEditable(params);
                    },
                  },
                  editable: (params) => {
                    return isSizeOrQuantityEditable(params);
                  },
                  resizable: true,
                  headerComponentParams: {
                    items: ["size", "stock", "quantity.abbr"],
                  },
                };
              });
            })(),
          ];
        }
        return prev;
      });
    }
  }, [
    t,
    tabIndex,
    isOrderEditable,
    sizeLength,
    isConfirmedEditable,
    getInflatedProductById,
    sheetKey,
    updateInflatedProduct,
    isSizeOrQuantityEditable,
  ]);

  useEffect(() => {
    setColDefs((prev) => {
      return produce(prev, (draft) => {
        const i = draft.findIndex(
          (colDef) => colDef.field === "searchLinkIcons"
        );
        if (i > -1) {
          draft[i].hide = lineSheetSetState?.lineSheetSet.type == "SEASON";
        }
      });
    });
  }, [lineSheetSetState?.lineSheetSet.type]);

  const onCellEditRequest = useCallback(
    async (event: CellEditRequestEvent<FlatProduct>) => {
      const data = event.data;
      const colId = event.column.getColId();
      const rowIndex = event.rowIndex === null ? -1 : event.rowIndex;
      const moldIndex = rowIndex % 3;

      const inflatedProduct = getInflatedProductById(
        sheetKey,
        tabIndex,
        data.id
      );

      if (inflatedProduct) {
        if (colId.startsWith("sq")) {
          // 수량이 변경된 경우

          let newValue = event.newValue;
          if (isNaN(newValue) || newValue === null || newValue === undefined) {
            newValue = 0;
          }
          const colIndex = parseInt(colId.split("_")[1]);
          const newStock = inflatedProduct.newStockWithOptionList
            ? inflatedProduct.newStockWithOptionList[colIndex].quantity
            : null;
          if (
            moldIndex === 2 &&
            newStock != null &&
            newValue > newStock &&
            newStock !== -1
          ) {
            showWarning(
              t("common:apply_order_quantity.messages.error.out_of_range"),
              "apply_price_condition_form"
            );
            return;
          }
          const nextData = produce(inflatedProduct, (draft) => {
            if (moldIndex === 1 && draft.newStockWithOptionList) {
              draft.newStockWithOptionList[colIndex].quantity = newValue;
            } else if (moldIndex === 2) {
              draft.orderQuantityWithOptionList[colIndex].quantity = newValue;
            }
          });

          updateInflatedProduct(sheetKey, tabIndex, nextData);
          onUpdatingInflatedProductComplete(true);
        } else if (colId === "priceAdjustedByPartial") {
          // condition이 변경된 경우

          if (moldIndex === 0) {
            //priceAdjustedByPartial
            const nextData = produce(inflatedProduct, (draft) => {
              if (draft.confirmedPriceAdjustedBy) {
                draft.confirmedPriceAdjustedBy.by = event.newValue;
              } else {
                draft.priceAdjustedBy.by = event.newValue;
              }
            });
            updateInflatedProduct(sheetKey, tabIndex, nextData);
            onUpdatingInflatedProductComplete(true);
            return;
          } else if (moldIndex === 1) {
            //nan이면 0처리하자
            let newValue = event.newValue;

            if (
              isNaN(newValue) ||
              newValue === null ||
              newValue === undefined
            ) {
              newValue = 0;
            }

            if (newValue > 100 || newValue < -100) {
              showWarning(
                t(
                  "common:apply_price_condition_form.messages.error.out_of_range"
                ),
                "apply_price_condition_form"
              );
              return;
            }
            const nextData = produce(inflatedProduct, (draft) => {
              if (draft.confirmedPriceAdjustedBy) {
                draft.confirmedPriceAdjustedBy.value = newValue;
              } else {
                draft.priceAdjustedBy.value = newValue;
              }
            });
            updateInflatedProduct(sheetKey, tabIndex, nextData);
            onUpdatingInflatedProductComplete(true);
          } else if (moldIndex === 2) {
            const nextData = produce(inflatedProduct, (draft) => {
              const stockData =
                draft.newStockWithOptionList ||
                draft.stockQuantityWithOptionList;

              const orderData = draft.orderQuantityWithOptionList;

              draft.orderQuantityWithOptionList = stockData.map(
                (stockDatum, index) => {
                  const orderQuantity = orderData[index].quantity;
                  return {
                    name: stockDatum.name,
                    quantity:
                      orderQuantity < stockDatum.quantity
                        ? stockDatum.quantity
                        : orderQuantity,
                  };
                }
              );
            });

            updateInflatedProduct(sheetKey, tabIndex, nextData);
            onUpdatingInflatedProductComplete(true);
          }
        }
      }
    },
    [
      t,
      showWarning,
      sheetKey,
      tabIndex,
      getInflatedProductById,
      updateInflatedProduct,
      onUpdatingInflatedProductComplete,
    ]
  );

  useEffect(() => {
    //page loading => row 변경인데, loading끝 -> rowData미적용 과정이 있네;
    if (gridRef.current && gridRef.current.api !== undefined) {
      const api = gridRef.current.api;
      if (isLoading || rowData === undefined) {
        api.showLoadingOverlay();
      } else if (rowData.length > 0) {
        api.hideOverlay();
      }
    }
  }, [isGridRefSet, isLoading, rowData]);

  const handleGridReady = useCallback(
    (event: GridReadyEvent<FlatProduct>) => {
      onGridReady(event);
    },
    [onGridReady]
  );

  const setInflatedTabsFamily = useSetRecoilState(inflatedTabsFamily(sheetKey));

  /* AG Grid는 cell value의 변경을 감지하려면 onCellEditRequest 이벤트를 사용해야하는데,
   * onCellEditRequest 는 value의 변경이 완료된 이후 발생하는 이벤트임. (커서를 다른 셀로 옮기거나 엔터를 누르거나 등)
   * 따라서 value의 변경을 즉시 감지하려면 onCellKeyDown 이벤트를 사용해야함.
   * onCellValueChanged 콜백이 있으나 동작하지 않음. (https://www.ag-grid.com/javascript-data-grid/grid-events/#reference-editing-cellValueChanged)
   */
  const onCellKeyDown = useCallback(
    (e: CellKeyDownEvent) => {
      if (onEditing) {
        const keyboardEvent = e.event as KeyboardEvent;
        const colDef = e.column.getColDef();
        const isEditable =
          typeof colDef.editable === "function"
            ? colDef.editable(e)
            : colDef.editable === true;
        // 숫자, 백스페이스, . 입력시에만 값이 변경되었다고 보고 isDirty를 true로 변경
        if (
          isEditable &&
          (!isNaN(keyboardEvent.key as any) ||
            ["Backspace", "."].includes(keyboardEvent.key))
        ) {
          setInflatedTabsFamily((prev) => {
            if (prev) {
              const newTabs = prev.tabs.map((tab) => {
                return {
                  ...tab,
                  isDirty: true,
                };
              });
              return {
                key: sheetKey,
                tabs: newTabs,
              };
            }
          });
        }
      }
    },
    [onEditing, setInflatedTabsFamily, sheetKey]
  );

  const onCellEditingStarted = useCallback(
    (e: CellEditingStartedEvent) => {
      if (onEditing) {
        onEditing(true);
      }
    },
    [onEditing]
  );

  const onCellEditingStopped = useCallback(
    (e: CellEditingStoppedEvent) => {
      if (onEditing) {
        onEditing(false);
      }
      // 값이 바뀌지 않은 편집 종료에서는 onCellEditRequest가 발사되지 않아
      // 저장 클릭 시 코디네이션 플래그가 안 올라가는 문제가 있어 여기서도 마킹.
      onUpdatingInflatedProductComplete(true);
    },
    [onEditing, onUpdatingInflatedProductComplete]
  );

  const defaultColDef = useMemo((): ColDef<FlatProduct> => {
    return {
      autoHeight: false,
      cellStyle: { fontSize: "12px", padding: "0" },
    };
  }, []);

  const handleSetGridRef = useCallback(
    (ref: AgGridReact<FlatProduct> | null) => {
      gridRef.current = ref;
      if (ref && !isGridRefSet) {
        setIsGridRefSet(true);
      }
    },
    [isGridRefSet, setIsGridRefSet]
  );

  const isRowSelectable = useCallback((node: IRowNode) => {
    const moldIndex =
      node != null && node.rowIndex != null ? node.rowIndex % 3 : -1;
    const rowIndex = node.rowIndex;

    if (
      moldIndex === 0 &&
      rowIndex !== null &&
      node.parent?.childrenAfterFilter?.[rowIndex]?.data?.priceAmount && // supply price
      !node.parent?.childrenAfterFilter?.[rowIndex + 1]?.data?.priceAmount && // cost
      !node.parent?.childrenAfterFilter?.[rowIndex + 2]?.data?.priceAmount // retail
    ) {
      // 만약 cost/retail이 없고 supply price만 있다면 컨디션 조작 불가능. 최종 공급가가 확정된 경우이기 때문.
      // 원래 isRowSelectable 함수에서 false 리턴 만으로 row의 선택 가능 여부를 판단할 수 있어야할 것 같은데, 제어가 제대로 안됨.
      // node.selectable 에 값을 직접 입력해주어야 함.
      // node의 생성 시점에 설정하고 싶으나 node는 ag-grid가 자동으로 생성하는 것 같음.
      // 여기서 false로 설정해주었더라도 ProductCheckboxCell 에서도 한 번 더 selectable을 체크하지 않으면 최초 로딩 시 체크박스가 선택 가능함.
      node.selectable = false;
      return false;
    }
    return true;
  }, []);

  const [checkedProductIds, setCheckedProductIds] = useState<number[]>([]);

  const handleSelectionChanged = useCallback(
    (event: SelectionChangedEvent<FlatProduct>) => {
      const selectedIds =
        uniq(
          event.api.getSelectedRows().map((row) => {
            return row.id;
          })
        )
      setCheckedProductIds(selectedIds);
      onSelectedProductIdsChange(selectedIds);
      event.api.refreshCells({
        force: true,
        columns: ["isChecked"],
      });
    },
    [onSelectedProductIdsChange]
  );

  const handleMouseOver = useCallback(
    (event: CellMouseOverEvent<FlatProduct>) => {
      const rowIndex = event.node.rowIndex;
      if (rowIndex !== null && rowIndex % 3 === 0) {
        if (
          event.colDef.headerClass &&
          (event.colDef.headerClass
              .toString()
              .includes("product-checkbox-header") ||
            event.colDef.headerClass
              .toString()
              .includes("product-image-header"))
        ) {
          const agPinnedLeftColsElement = document.querySelector(
            ".ag-pinned-left-cols-container"
          );
          if (agPinnedLeftColsElement !== null) {
            const secondRowElement = agPinnedLeftColsElement.querySelector(
              `div.ag-row[row-index="${rowIndex + 1}"]`
            );
            const thirdRowElement = secondRowElement?.nextElementSibling;
            secondRowElement?.classList.add("ag-row-hover");
            thirdRowElement?.classList.add("ag-row-hover");
          }
          const agCenterColsElement = document.querySelector(
            ".ag-center-cols-container"
          );
          if (agCenterColsElement !== null) {
            const secondRowElement = agCenterColsElement.querySelector(
              `div.ag-row[row-index="${rowIndex + 1}"]`
            );
            const thirdRowElement = secondRowElement?.nextElementSibling;
            secondRowElement?.classList.add("ag-row-hover");
            thirdRowElement?.classList.add("ag-row-hover");
          }
        }
      }
    },
    []
  );

  const handleMouseOut = useCallback(
    (event: CellMouseOutEvent<FlatProduct>) => {
      const rowIndex = event.node.rowIndex;
      if (rowIndex !== null && rowIndex % 3 === 0) {
        if (
          event.colDef.headerClass &&
          (event.colDef.headerClass
              .toString()
              .includes("product-checkbox-header") ||
            event.colDef.headerClass
              .toString()
              .includes("product-image-header"))
        ) {
          const agPinnedLeftColsElement = document.querySelector(
            ".ag-pinned-left-cols-container"
          );
          if (agPinnedLeftColsElement !== null) {
            const secondRowElement = agPinnedLeftColsElement.querySelector(
              `div.ag-row[row-index="${rowIndex + 1}"]`
            );
            const thirdRowElement = secondRowElement?.nextElementSibling;
            secondRowElement?.classList.remove("ag-row-hover");
            thirdRowElement?.classList.remove("ag-row-hover");
          }
          const agCenterColsElement = document.querySelector(
            ".ag-center-cols-container"
          );
          if (agCenterColsElement !== null) {
            const secondRowElement = agCenterColsElement.querySelector(
              `div.ag-row[row-index="${rowIndex + 1}"]`
            );
            const thirdRowElement = secondRowElement?.nextElementSibling;
            secondRowElement?.classList.remove("ag-row-hover");
            thirdRowElement?.classList.remove("ag-row-hover");
          }
        }
      }
    },
    []
  );

  useEffect(() => {
    // globalSearchValue 가 변경될 때마다 강제로 리렌더링
    gridRef.current?.api?.refreshCells({
      force: true, // cell 렌더러 다시 호출
    });
  }, [query]);

  const isFiltered = Boolean(
    (query.brandName__eq && query.brandName__eq.length > 0) ||
    query.gender__eq ||
    query.category__eq ||
    query.season__in ||
    query.query
  );

  return (
    <Box
      className={"ag-theme-alpine"}
      paddingBottom={"48px"}
      height={"100%"}
      width={"100%"}
      fontFamily={"Inter"}
      position={"relative"}
    >
      <AgGridReact<FlatProduct>
        ref={handleSetGridRef}
        suppressRowTransform={true}
        readOnlyEdit={true}
        // suppressClickEdit={true}
        navigateToNextCell={() => null}
        // singleClickEdit={true}
        getRowId={getRowId}
        headerHeight={24}
        columnDefs={colDefs}
        rowData={rowData}
        //왜 25를 해야 24가 되는거야?;;
        rowHeight={25}
        rowModelType={"clientSide"}
        onCellKeyDown={onCellKeyDown}
        onCellEditRequest={onCellEditRequest}
        onCellEditingStarted={onCellEditingStarted}
        onCellEditingStopped={onCellEditingStopped}
        defaultColDef={defaultColDef}
        onGridReady={handleGridReady}
        rowSelection={"multiple"}
        isRowSelectable={isRowSelectable}
        suppressRowClickSelection={true}
        onSelectionChanged={handleSelectionChanged}
        onCellMouseOver={handleMouseOver}
        onCellMouseOut={handleMouseOut}
        enableCellTextSelection={true}
        components={components}
        suppressRowHoverHighlight={false}
        columnHoverHighlight={true}
        suppressMovableColumns={true}
        noRowsOverlayComponent={NoResults}
        noRowsOverlayComponentParams={{
          isBrandFiltered: query.brandName__eq && query.brandName__eq.length > 0,
          handleResetFilter: () => {
            setOrderSheetSearchKeyword("");
            setQuery(
              (prev) => {
                return {
                  ...prev,
                  ...EMPTY_SHEET_TAB_FILTER_QUERY,
                };
              },
              {
                replace: true,
              }
            );
          },
        }}
        animateRows={false}
        rowBuffer={10}
        suppressRowVirtualisation={false}
        suppressColumnVirtualisation={false}
        suppressDragLeaveHidesColumns={true}
      />
      <OrderSheetTabBottomBar
        sheetKey={sheetKey}
        exchangeCurrencyValue={exchangeCurrencyValue}
        selectedCurrency={selectedCurrency}
        checkedProductIds={checkedProductIds}
        isFiltered={isFiltered}
        orderStatus={"ORDERED"}
        commission={lineSheetSetState?.lineSheetSet?.commission}
        commissionType={lineSheetSetState?.lineSheetSet?.commissionType}
      />
    </Box>
  );
}

// OrderSheetTab.whyDidYouRender = true;

export default OrderSheetTab;
